import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { toast } from 'react-toastify';
import { updatePageLayoutSettings, updateVisionSection, uploadBannerImageAndGetUrl } from '../../store/AsyncMethods/SetupMethod';
import backendVerifiedUser from '../../api/backendVerifiedUser';

const EMPTY_TILE = { image: '', title: '', link: '' };

const DEFAULT_HOME_SECTION_ORDER = ['shop_by_category', 'vision', 'featured', 'collections', 'recently_viewed'];
const HOME_SECTION_META = {
  shop_by_category: { title: 'Shop By Category', description: 'Category slider that floats over the hero banner' },
  vision: { title: 'Vision Section', description: 'Large hero image + 2×2 tile grid' },
  featured: { title: 'Our Feature Products', description: 'Bold masonry grid of featured products' },
  collections: { title: 'Discover Our Collections', description: 'Staggered offset cards for collections' },
  recently_viewed: { title: 'Recently Viewed', description: 'Product history section — appears on both Classic and Modern layouts' },
};

const PageLayoutManagement = () => {
  const dispatch = useDispatch();

  const [showProducts, setShowProducts] = useState(0);
  const [showCollections, setShowCollections] = useState(0);
  const [useFrontpageFilter, setUseFrontpageFilter] = useState(1);
  const [showShopByCategory, setShowShopByCategory] = useState(1);
  const [shopByCategoryTitle, setShopByCategoryTitle] = useState('Shop By Category');
  const [shopByCategorySubtitle, setShopByCategorySubtitle] = useState('Discover our handpicked collections');
  const [showRecentlyViewed, setShowRecentlyViewed] = useState(1);
  const [homeSectionOrder, setHomeSectionOrder] = useState(DEFAULT_HOME_SECTION_ORDER);
  const [loading, setLoading] = useState(false);

  // Vision section (large hero image + 2x2 tile grid) — modern layout only
  const [visionEnabled, setVisionEnabled] = useState(0);
  const [visionHeading, setVisionHeading] = useState('Vision 2025');
  const [visionSubheading, setVisionSubheading] = useState('Rethinking Possibilities');
  const [visionButtonText, setVisionButtonText] = useState('Read more');
  const [visionButtonLink, setVisionButtonLink] = useState('/shop');
  const [visionImage, setVisionImage] = useState('');
  const [visionTiles, setVisionTiles] = useState([
    { ...EMPTY_TILE }, { ...EMPTY_TILE }, { ...EMPTY_TILE }, { ...EMPTY_TILE },
  ]);
  const [visionSaving, setVisionSaving] = useState(false);
  const [visionUploadingIndex, setVisionUploadingIndex] = useState(null); // null | 'hero' | 0..3
  const heroFileInputRef = useRef(null);
  const tileFileInputRefs = useRef([]);

  const applySetupToState = (d) => {
    if (!d) return;
    setShowProducts(d.show_products != null ? Number(d.show_products) : 0);
    setShowCollections(d.show_collections != null ? Number(d.show_collections) : 0);
    setUseFrontpageFilter(d.use_frontpage_filter != null ? Number(d.use_frontpage_filter) : 1);
    setShowShopByCategory(d.show_shop_by_category != null ? Number(d.show_shop_by_category) : 1);
    setShopByCategoryTitle(d.shop_by_category_title || 'Shop By Category');
    setShopByCategorySubtitle(d.shop_by_category_subtitle || 'Discover our handpicked collections');
    setShowRecentlyViewed(d.show_recently_viewed != null ? Number(d.show_recently_viewed) : 1);

    const savedOrder = Array.isArray(d.home_section_order) ? d.home_section_order : [];
    const validSaved = savedOrder.filter(
      (k, i) => DEFAULT_HOME_SECTION_ORDER.includes(k) && savedOrder.indexOf(k) === i
    );
    const missing = DEFAULT_HOME_SECTION_ORDER.filter((k) => !validSaved.includes(k));
    setHomeSectionOrder(validSaved.length > 0 ? [...validSaved, ...missing] : DEFAULT_HOME_SECTION_ORDER);

    const vs = d.vision_section || {};
    setVisionEnabled(vs.enabled ? 1 : 0);
    setVisionHeading(vs.heading || 'Vision 2025');
    setVisionSubheading(vs.subheading || 'Rethinking Possibilities');
    setVisionButtonText(vs.buttonText || 'Read more');
    setVisionButtonLink(vs.buttonLink || '/shop');
    setVisionImage(vs.image || '');
    const savedTiles = Array.isArray(vs.tiles) ? vs.tiles : [];
    setVisionTiles([0, 1, 2, 3].map((i) => savedTiles[i] || { ...EMPTY_TILE }));
  };

  // Direct API call — Redux bypass karo taake overwrite na ho
  useEffect(() => {
    backendVerifiedUser.get('/setup-details').then((res) => {
      applySetupToState(res.data);
    }).catch(() => {});
  }, []);

  const handleVisionUpload = async (file, target) => {
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Unsupported file type. Please upload PNG, JPEG, WEBP, or GIF.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      return;
    }
    setVisionUploadingIndex(target);
    const formData = new FormData();
    formData.append("banner", file);
    const url = await dispatch(uploadBannerImageAndGetUrl(formData));
    if (url) {
      if (target === 'hero') {
        setVisionImage(url);
      } else {
        setVisionTiles((prev) => prev.map((t, i) => (i === target ? { ...t, image: url } : t)));
      }
      toast.success("Image uploaded successfully.");
    } else {
      toast.error("Failed to upload image.");
    }
    setVisionUploadingIndex(null);
  };

  const updateTile = (index, field, value) => {
    setVisionTiles((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const handleVisionSave = async () => {
    setVisionSaving(true);
    const payload = {
      enabled: visionEnabled ? 1 : 0,
      heading: visionHeading,
      subheading: visionSubheading,
      buttonText: visionButtonText,
      buttonLink: visionButtonLink,
      image: visionImage,
      tiles: visionTiles,
    };
    try {
      const success = await dispatch(updateVisionSection(payload));
      if (success) {
        toast.success('Vision section saved successfully');
      } else {
        toast.error('Failed to save Vision section');
      }
    } catch {
      toast.error('An error occurred while saving');
    } finally {
      setVisionSaving(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const data = {
      show_products: showProducts ? 1 : 0,
      show_collections: showCollections ? 1 : 0,
      use_frontpage_filter: useFrontpageFilter ? 1 : 0,
      show_shop_by_category: showShopByCategory ? 1 : 0,
      home_section_order: homeSectionOrder,
      shop_by_category_title: shopByCategoryTitle,
      shop_by_category_subtitle: shopByCategorySubtitle,
      show_recently_viewed: showRecentlyViewed ? 1 : 0,
    };
    try {
      const result = await dispatch(updatePageLayoutSettings(data));
      if (result) {
        toast.success('Settings saved successfully');
        // Backend se confirmed values directly state mein set karo
        if (result && typeof result === 'object') {
          setShowProducts(result.show_products != null ? Number(result.show_products) : data.show_products);
          setShowCollections(result.show_collections != null ? Number(result.show_collections) : data.show_collections);
          setUseFrontpageFilter(result.use_frontpage_filter != null ? Number(result.use_frontpage_filter) : data.use_frontpage_filter);
          setShowShopByCategory(result.show_shop_by_category != null ? Number(result.show_shop_by_category) : data.show_shop_by_category);
          setShopByCategoryTitle(result.shop_by_category_title || data.shop_by_category_title);
          setShopByCategorySubtitle(result.shop_by_category_subtitle || data.shop_by_category_subtitle);
          setShowRecentlyViewed(result.show_recently_viewed != null ? Number(result.show_recently_viewed) : data.show_recently_viewed);
          if (Array.isArray(result.home_section_order) && result.home_section_order.length > 0) {
            setHomeSectionOrder(result.home_section_order);
          }
        }
      } else {
        toast.error('Failed to save settings');
      }
    } catch {
      toast.error('An error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  const onSectionDragEnd = (result) => {
    if (!result.destination) return;
    const arr = Array.from(homeSectionOrder);
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setHomeSectionOrder(arr);
  };

  const handleReset = () => {
    backendVerifiedUser.get('/setup-details').then((res) => {
      applySetupToState(res.data);
      toast.info('Settings reset to saved values');
    }).catch(() => {});
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Page Layout Management</h2>
        <p className="text-gray-600">Configure which features and sections to display on your website</p>
      </div>

      <Card className="mb-4">
        <h3 className="text-lg font-semibold text-gray-700 mb-1">Feature Visibility &amp; Order</h3>
        <p className="text-sm text-gray-500 mb-4">Drag to reorder — the sequence here is exactly how these sections appear on the homepage.</p>
        <Divider />

        <div className="flex flex-col gap-4 mt-4">
          <DragDropContext onDragEnd={onSectionDragEnd}>
            <Droppable droppableId="home-sections">
              {(provided) => (
                <div className="flex flex-col gap-3" ref={provided.innerRef} {...provided.droppableProps}>
                  {homeSectionOrder.map((key, index) => {
                    const meta = HOME_SECTION_META[key];
                    if (!meta) return null;
                    const checked = key === 'featured' ? showProducts === 1
                      : key === 'collections' ? showCollections === 1
                      : key === 'shop_by_category' ? showShopByCategory === 1
                      : key === 'recently_viewed' ? showRecentlyViewed === 1
                      : visionEnabled === 1;
                    const onToggle = (e) => {
                      const val = e.checked ? 1 : 0;
                      if (key === 'featured') setShowProducts(val);
                      else if (key === 'collections') setShowCollections(val);
                      else if (key === 'shop_by_category') setShowShopByCategory(val);
                      else if (key === 'recently_viewed') setShowRecentlyViewed(val);
                      else setVisionEnabled(val);
                    };
                    return (
                      <Draggable key={key} draggableId={key} index={index}>
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`flex flex-col gap-3 p-3 border rounded-lg transition-colors ${snapshot.isDragging ? 'border-blue-400 bg-blue-50 shadow-lg' : 'border-gray-200 hover:bg-gray-50'}`}
                          >
                            <div className="flex items-center gap-3">
                              <span {...dragProvided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-400 px-1" title="Drag to reorder">
                                <i className="pi pi-bars" />
                              </span>
                              <Checkbox
                                inputId={`section_${key}`}
                                checked={checked}
                                onChange={onToggle}
                                className="p-checkbox"
                              />
                              <label htmlFor={`section_${key}`} className="cursor-pointer flex-1">
                                <div className="font-semibold text-gray-800">{meta.title}</div>
                                <div className="text-sm text-gray-600">{meta.description}</div>
                              </label>
                              <span className="text-xs font-mono text-gray-400">#{index + 1}</span>
                            </div>

                            {key === 'shop_by_category' && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-9">
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs text-gray-500">Title</label>
                                  <InputText
                                    value={shopByCategoryTitle}
                                    onChange={(e) => setShopByCategoryTitle(e.target.value)}
                                    placeholder="Shop By Category"
                                  />
                                </div>
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs text-gray-500">Subtitle</label>
                                  <InputText
                                    value={shopByCategorySubtitle}
                                    onChange={(e) => setShopByCategorySubtitle(e.target.value)}
                                    placeholder="Discover our handpicked collections"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Divider align="left">
            <span className="text-sm text-gray-500 font-medium">Product Display Mode</span>
          </Divider>

          <div className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-colors ${useFrontpageFilter === 1 ? 'border-blue-400 bg-blue-50' : 'border-orange-400 bg-orange-50'}`}>
            <Checkbox
              inputId="use_frontpage_filter"
              checked={useFrontpageFilter === 1}
              onChange={(e) => setUseFrontpageFilter(e.checked ? 1 : 0)}
              className="p-checkbox mt-1"
            />
            <label htmlFor="use_frontpage_filter" className="cursor-pointer flex-1">
              <div className="font-semibold text-gray-800 flex items-center gap-2">
                Show only "Frontpage" marked products
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${useFrontpageFilter === 1 ? 'bg-blue-500 text-white' : 'bg-orange-500 text-white'}`}>
                  {useFrontpageFilter === 1 ? 'FILTER ON' : 'ALGORITHM MODE'}
                </span>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {useFrontpageFilter === 1
                  ? '✅ Sirf woh products dikhaye jayenge jinpar admin ne "Show on Frontpage" ON kiya hua hai (product table mein)'
                  : '🤖 Sab active products available honge — "You May Also Like" algorithm user ki history dekh ke suggest karega'}
              </div>
            </label>
          </div>
        </div>
      </Card>

      <Card className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-700">Vision Section</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${visionEnabled === 1 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-700'}`}>
            {visionEnabled === 1 ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-2">
          Large hero image with a heading/button on the left, and a 2×2 grid of image tiles on the right.
          Modern layout only.
        </p>
        <Divider />

        <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors mb-4">
          <Checkbox
            inputId="vision_enabled"
            checked={visionEnabled === 1}
            onChange={(e) => setVisionEnabled(e.checked ? 1 : 0)}
            className="p-checkbox"
          />
          <label htmlFor="vision_enabled" className="cursor-pointer flex-1">
            <div className="font-semibold text-gray-800">Show Vision Section on Homepage</div>
            <div className="text-sm text-gray-600">Enable this to display the section on the storefront (modern theme).</div>
          </label>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Hero side */}
          <div className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg">
            <div className="font-semibold text-gray-700">Hero (left side)</div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600">Heading</label>
              <InputText value={visionHeading} onChange={(e) => setVisionHeading(e.target.value)} placeholder="Vision 2025" />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600">Subheading</label>
              <InputText value={visionSubheading} onChange={(e) => setVisionSubheading(e.target.value)} placeholder="Rethinking Possibilities" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-600">Button Text</label>
                <InputText value={visionButtonText} onChange={(e) => setVisionButtonText(e.target.value)} placeholder="Read more" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-600">Button Link</label>
                <InputText value={visionButtonLink} onChange={(e) => setVisionButtonLink(e.target.value)} placeholder="/shop" />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600">Hero Image</label>
              <div className="flex items-center gap-3">
                {visionImage && (
                  <img src={visionImage} alt="Hero" className="w-16 h-16 object-cover rounded border border-gray-200" />
                )}
                <Button
                  type="button"
                  label={visionUploadingIndex === 'hero' ? 'Uploading...' : 'Upload'}
                  icon={visionUploadingIndex === 'hero' ? 'pi pi-spin pi-spinner' : 'pi pi-upload'}
                  className="p-button-outlined"
                  disabled={visionUploadingIndex !== null}
                  onClick={() => heroFileInputRef.current?.click()}
                />
                <input
                  ref={heroFileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    handleVisionUpload(file, 'hero');
                  }}
                />
              </div>
              <small className="text-gray-500">Recommended: tall portrait image, e.g. 900×1200px</small>
            </div>
          </div>

          {/* Tiles side */}
          <div className="flex flex-col gap-3">
            <div className="font-semibold text-gray-700">Tiles (right side, 2×2 grid)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {visionTiles.map((tile, index) => (
                <div key={index} className="flex flex-col gap-2 p-3 border border-gray-200 rounded-lg">
                  <div className="text-xs font-semibold text-gray-500">Tile {index + 1}</div>
                  <InputText
                    value={tile.title}
                    onChange={(e) => updateTile(index, 'title', e.target.value)}
                    placeholder={`e.g. For Fashion`}
                  />
                  <InputText
                    value={tile.link}
                    onChange={(e) => updateTile(index, 'link', e.target.value)}
                    placeholder="/shop or /category/5"
                  />
                  <div className="flex items-center gap-2">
                    {tile.image && (
                      <img src={tile.image} alt={tile.title || `Tile ${index + 1}`} className="w-12 h-12 object-cover rounded border border-gray-200" />
                    )}
                    <Button
                      type="button"
                      label={visionUploadingIndex === index ? 'Uploading...' : 'Upload'}
                      icon={visionUploadingIndex === index ? 'pi pi-spin pi-spinner' : 'pi pi-upload'}
                      className="p-button-outlined p-button-sm"
                      disabled={visionUploadingIndex !== null}
                      onClick={() => tileFileInputRefs.current[index]?.click()}
                    />
                    <input
                      ref={(el) => (tileFileInputRefs.current[index] = el)}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        handleVisionUpload(file, index);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <Divider align="left">
          <span className="text-sm text-gray-500 font-medium">Preview</span>
        </Divider>
        <div className="rounded-lg overflow-hidden border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div
              className="relative flex items-end p-6"
              style={{
                minHeight: 260,
                backgroundImage: visionImage ? `url(${visionImage})` : undefined,
                backgroundColor: visionImage ? undefined : '#e5e7eb',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {!visionImage && (
                <span className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                  Hero image preview
                </span>
              )}
              <div className="relative text-white">
                {visionSubheading && <div className="text-xl font-light">{visionSubheading.split(' ')[0]}<span className="italic">{visionSubheading.split(' ').slice(1).join(' ')}</span></div>}
                {visionHeading && <div className="text-2xl font-bold">{visionHeading}</div>}
                {visionButtonText && (
                  <span className="inline-block mt-2 px-4 py-1.5 border border-white rounded-full text-sm">
                    {visionButtonText}
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2">
              {visionTiles.map((tile, index) => (
                <div key={index} className="p-3 bg-white border border-gray-100 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-800">{tile.title || `Tile ${index + 1}`}</span>
                    <span className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs">→</span>
                  </div>
                  <div
                    className="rounded"
                    style={{
                      height: 90,
                      backgroundImage: tile.image ? `url(${tile.image})` : undefined,
                      backgroundColor: tile.image ? undefined : '#f3f4f6',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            label="Save Vision Section"
            icon="pi pi-check"
            className="p-button-primary"
            onClick={handleVisionSave}
            loading={visionSaving}
          />
        </div>
      </Card>

      <div className="flex gap-3 justify-end">
        <Button
          label="Reset"
          icon="pi pi-refresh"
          className="p-button-secondary"
          onClick={handleReset}
          disabled={loading}
        />
        <Button
          label="Save Changes"
          icon="pi pi-check"
          className="p-button-primary"
          onClick={handleSave}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default PageLayoutManagement;
